import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  FileText, Briefcase, MessageSquare, DollarSign, 
  BookOpen, ChevronRight, Download, Sparkles, ArrowRight,
  GraduationCap, FolderOpen, Lock
} from 'lucide-react';

// Professional Communication Guides - Modules 1-8
const professionalCommunicationGuides = [
  { 
    title: 'Module 1: Foundations of Professional Communication', 
    description: 'Build a strong foundation for effective workplace communication',
    file: '/resources/Module 1-Foundations of Professional Communication.docx',
    type: 'docx'
  },
  { 
    title: 'Module 2: Email & Written Communication Excellence', 
    description: 'Master email etiquette and professional writing skills',
    file: '/resources/Module 2-Email & Written Communication Excellence.docx',
    type: 'docx'
  },
  { 
    title: 'Module 3: Verbal & In-Person Communication', 
    description: 'Enhance your verbal and face-to-face communication skills',
    file: '/resources/Module 3-Verbal & In-Person Communication.docx',
    type: 'docx'
  },
  { 
    title: 'Module 4: Professional Communication for Job Seekers', 
    description: 'Communication strategies for the job search process',
    file: '/resources/Module 4-Professional Communication for Job Seekers.docx',
    type: 'docx'
  },
  { 
    title: 'Module 5: Communicating Career Changes with Confidence', 
    description: 'Navigate career transitions with effective communication',
    file: '/resources/Module 5-Communicating Career Changes with Confidence.docx',
    type: 'docx'
  },
  { 
    title: 'Module 6: Workplace Boundaries', 
    description: 'Learn to set and communicate healthy workplace boundaries',
    file: '/resources/Moduel 6-Workplace Boundaries.docx',
    type: 'docx'
  },
  { 
    title: 'Module 7: Digital & Virtual Communication', 
    description: 'Excel in digital and remote communication environments',
    file: '/resources/Module 7-Digital & Virtual Communication.docx',
    type: 'docx'
  },
  { 
    title: 'Module 8: Executive Communication', 
    description: 'Develop executive-level communication skills',
    file: '/resources/Module 8-Executive Communication.docx',
    type: 'docx'
  },
];

// Career Planning Resources
const careerPlanningResources = [
  { 
    title: 'Career Planning Step 1: Audit Your Current Reality', 
    description: 'Start your career planning journey with a self-assessment',
    file: '/resources/Career Planning Step 1-Audit Your Current Reality.docx',
    type: 'docx'
  },
  { 
    title: 'Personalized Career Planning Overview', 
    description: 'Comprehensive guide to creating your personalized career plan',
    file: '/resources/Personalized Career Planning Overview.docx',
    type: 'docx'
  },
  { 
    title: 'Salary Awareness Overview', 
    description: 'Understand your worth and navigate salary conversations',
    file: '/resources/Salary Awareness Overview.docx',
    type: 'docx'
  },
];

// Resume & Interview Resources
const resumeInterviewResources = [
  { 
    title: 'Resume Template', 
    description: 'Professional resume template to showcase your experience',
    file: '/resources/Resume Template.pdf',
    type: 'pdf'
  },
  { 
    title: 'Interview Prep Guide', 
    description: 'Comprehensive guide to ace your interviews',
    file: '/resources/Interview Prep.pdf',
    type: 'pdf'
  },
  { 
    title: 'Sample Resume - Executive Assistant', 
    description: 'Example resume for administrative professionals',
    file: '/resources/Resume - Executive Assistant-Sample.pdf',
    type: 'pdf'
  },
  { 
    title: 'Sample Resume - Nurse', 
    description: 'Example resume for healthcare professionals',
    file: '/resources/Resume - Nurse-Sample.pdf',
    type: 'pdf'
  },
  { 
    title: 'Sample Resume - Plumber/Tech', 
    description: 'Example resume for trade and technical professionals',
    file: '/resources/Plumber Tech Resume-Sample.pdf',
    type: 'pdf'
  },
  { 
    title: 'Sample Resume - Student Internship', 
    description: 'Example resume for students seeking internships',
    file: '/resources/Student internship resume -sample.pdf',
    type: 'pdf'
  },
];

const libraryCategories = [
  { 
    icon: MessageSquare, 
    title: 'Professional Communication Guides', 
    description: 'Master workplace communication with our 8-module guide series',
    count: professionalCommunicationGuides.length,
    color: 'purple'
  },
  { 
    icon: Briefcase, 
    title: 'Career Planning Resources', 
    description: 'Map out your career goals and milestones',
    count: careerPlanningResources.length,
    color: 'teal'
  },
  { 
    icon: FileText, 
    title: 'Resume & Interview Templates', 
    description: 'Professional templates and sample resumes',
    count: resumeInterviewResources.length,
    color: 'purple'
  },
  { 
    icon: DollarSign, 
    title: 'Salary Awareness Tools', 
    description: 'Understand your worth and plan your growth',
    count: 1,
    color: 'teal'
  },
];

// Resource Download Card Component
const ResourceCard = ({ resource, index }) => {
  return (
    <a 
      href={resource.file}
      download
      className="group block"
    >
      <div className="flex items-center gap-4 p-4 rounded-xl bg-white border border-stone-100 hover:border-hr-purple/30 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110 ${
          resource.type === 'pdf' ? 'bg-red-100 group-hover:bg-red-500' : 'bg-blue-100 group-hover:bg-blue-500'
        }`}>
          <FileText className={`h-6 w-6 transition-colors duration-300 ${
            resource.type === 'pdf' ? 'text-red-600 group-hover:text-white' : 'text-blue-600 group-hover:text-white'
          }`} />
        </div>
        <div className="flex-grow min-w-0">
          <h4 className="font-medium text-hr-text-primary group-hover:text-hr-purple transition-colors duration-300 truncate">
            {resource.title}
          </h4>
          <p className="text-sm text-hr-text-muted truncate">{resource.description}</p>
        </div>
        <div className="flex-shrink-0">
          <Download className="h-5 w-5 text-hr-text-muted group-hover:text-hr-purple transition-colors duration-300" />
        </div>
      </div>
    </a>
  );
};

export const Resources = () => {
  const [expandedCategory, setExpandedCategory] = useState('communication');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const CORRECT_PASSWORD = 'Nest@2026';

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === CORRECT_PASSWORD) {
      setIsAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password. Please try again.');
    }
  };

  // Password Gate UI
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen overflow-hidden">
        {/* Hero Section */}
        <section className="py-24 bg-gradient-to-br from-hr-warm-bg via-white to-hr-teal-light/20 relative" data-testid="resources-hero">
          <div className="absolute top-0 right-0 w-96 h-96 bg-hr-teal/10 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-hr-purple/10 rounded-full blur-3xl animate-float-slow" />
          <div className="absolute top-1/2 left-1/4 w-40 h-40 bg-hr-teal/5 rounded-full blur-2xl animate-blob" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-teal/10 mb-6 animate-bounce-subtle">
                <Lock className="h-4 w-4 text-hr-teal animate-pulse" />
                <span className="text-hr-teal text-sm font-medium">Protected Content</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-hr-text-primary animate-fade-in-up">
                <span className="text-gradient bg-gradient-to-r from-hr-teal to-hr-purple bg-clip-text text-transparent animate-gradient">Resources</span> for Your Journey
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-hr-text-secondary leading-relaxed animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
                Enter the password to access our library of tools, templates, and guides.
              </p>
            </div>
          </div>
        </section>

        {/* Password Entry Section */}
        <section className="py-24 bg-white relative overflow-hidden">
          <div className="absolute top-1/2 left-0 w-64 h-64 bg-hr-purple/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow" />
          <div className="absolute top-1/2 right-0 w-64 h-64 bg-hr-teal/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow stagger-2" />
          
          <div className="relative max-w-md mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="border-0 shadow-2xl overflow-hidden animate-fade-in-up">
              <CardContent className="p-8 sm:p-12">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 mx-auto rounded-full bg-hr-purple-light flex items-center justify-center mb-6">
                    <Lock className="h-10 w-10 text-hr-purple" />
                  </div>
                  <h2 className="font-heading text-2xl font-semibold text-hr-text-primary mb-2">
                    Password Required
                  </h2>
                  <p className="text-hr-text-secondary">
                    Please enter the password to access the resources.
                  </p>
                </div>

                <form onSubmit={handlePasswordSubmit} className="space-y-6">
                  <div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      className="w-full px-4 py-4 rounded-xl border border-stone-200 focus:border-hr-purple focus:ring-2 focus:ring-hr-purple/20 outline-none transition-all duration-300 text-lg"
                      data-testid="resources-password-input"
                    />
                    {passwordError && (
                      <p className="mt-2 text-red-500 text-sm animate-fade-in-up">{passwordError}</p>
                    )}
                  </div>
                  <Button 
                    type="submit"
                    className="w-full bg-hr-purple hover:bg-hr-purple-dark text-white rounded-full py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1"
                    data-testid="resources-password-submit"
                  >
                    Access Resources
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-hr-warm-bg via-white to-hr-teal-light/20 relative" data-testid="resources-hero">
        <div className="absolute top-0 right-0 w-96 h-96 bg-hr-teal/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-hr-purple/10 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute top-1/2 left-1/4 w-40 h-40 bg-hr-teal/5 rounded-full blur-2xl animate-blob" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-hr-teal/10 mb-6 animate-bounce-subtle">
              <BookOpen className="h-4 w-4 text-hr-teal animate-pulse" />
              <span className="text-hr-teal text-sm font-medium">Knowledge Hub</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-hr-text-primary animate-fade-in-up">
              <span className="text-gradient bg-gradient-to-r from-hr-teal to-hr-purple bg-clip-text text-transparent animate-gradient">Resources</span> for Your Journey
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-hr-text-secondary leading-relaxed animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              Access our library of tools, templates, and guides designed to support your career and personal growth.
            </p>
          </div>
        </div>
      </section>

      {/* Library Section with Downloadable Resources */}
      <section id="library" className="py-24 bg-white scroll-mt-24 relative overflow-hidden" data-testid="resources-library">
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-hr-purple/5 rounded-full blur-3xl -translate-y-1/2 animate-pulse-slow" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-hr-purple-light flex items-center justify-center animate-bounce-subtle">
                <FolderOpen className="h-7 w-7 text-hr-purple" />
              </div>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-hr-text-primary animate-fade-in-up">
              Resource Library
            </h2>
            <p className="mt-4 text-lg text-hr-text-secondary animate-fade-in-up stagger-1" style={{ opacity: 0 }}>
              Downloadable tools and templates for your success
            </p>
          </div>

          {/* Category Overview Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {libraryCategories.map((category, index) => (
              <Card 
                key={index} 
                className={`group border border-stone-100 hover:border-hr-purple/30 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 overflow-hidden animate-fade-in-up ${category.comingSoon ? 'opacity-60' : 'cursor-pointer'}`}
                style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
                data-testid={`library-category-${index}`}
              >
                <CardContent className="p-6 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-hr-purple/5 to-hr-teal/5 opacity-0 group-hover:opacity-100 transition-all duration-500" />
                  
                  <div className={`relative w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-md group-hover:shadow-lg ${
                    category.color === 'purple' ? 'bg-gradient-to-br from-hr-purple-light to-hr-purple/20' : 'bg-gradient-to-br from-hr-teal-light to-hr-teal/20'
                  }`}>
                    <category.icon className={`h-7 w-7 ${
                      category.color === 'purple' ? 'text-hr-purple' : 'text-hr-teal'
                    }`} />
                  </div>
                  <h3 className="relative font-heading text-lg font-semibold text-hr-text-primary mb-2 group-hover:text-hr-purple transition-colors duration-300">
                    {category.title}
                  </h3>
                  <p className="relative text-sm text-hr-text-muted mb-3">
                    {category.description}
                  </p>
                  <div className="relative flex items-center justify-between">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      category.comingSoon ? 'bg-stone-100 text-stone-500' : category.color === 'purple' ? 'bg-hr-purple/10 text-hr-purple' : 'bg-hr-teal/10 text-hr-teal'
                    }`}>
                      {category.comingSoon ? 'Coming Soon' : `${category.count} resources`}
                    </span>
                  </div>
                  
                  <div className="absolute bottom-0 left-0 h-1 w-0 group-hover:w-full bg-gradient-to-r from-hr-purple to-hr-teal transition-all duration-500" />
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Expandable Resource Sections */}
          <div className="space-y-8">
            {/* Professional Communication Guides */}
            <div className="bg-gradient-to-br from-hr-purple-light/30 to-white rounded-3xl p-8 shadow-lg animate-fade-in-up" data-testid="communication-guides-section">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-hr-purple/10 flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-hr-purple" />
                </div>
                <div>
                  <h3 className="font-heading text-2xl font-semibold text-hr-text-primary">Professional Communication Guides</h3>
                  <p className="text-hr-text-muted">Modules 1-8: Complete guide series</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {professionalCommunicationGuides.map((resource, index) => (
                  <ResourceCard key={index} resource={resource} index={index} />
                ))}
              </div>
            </div>

            {/* Career Planning Resources */}
            <div className="bg-gradient-to-br from-hr-teal-light/30 to-white rounded-3xl p-8 shadow-lg animate-fade-in-up stagger-1" data-testid="career-planning-section">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-hr-teal/10 flex items-center justify-center">
                  <Briefcase className="h-6 w-6 text-hr-teal" />
                </div>
                <div>
                  <h3 className="font-heading text-2xl font-semibold text-hr-text-primary">Career Planning Resources</h3>
                  <p className="text-hr-text-muted">Plan and navigate your career journey</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {careerPlanningResources.map((resource, index) => (
                  <ResourceCard key={index} resource={resource} index={index} />
                ))}
              </div>
            </div>

            {/* Resume & Interview Resources */}
            <div className="bg-gradient-to-br from-hr-purple-light/30 to-white rounded-3xl p-8 shadow-lg animate-fade-in-up stagger-2" data-testid="resume-interview-section">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-hr-purple/10 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-hr-purple" />
                </div>
                <div>
                  <h3 className="font-heading text-2xl font-semibold text-hr-text-primary">Resume & Interview Templates</h3>
                  <p className="text-hr-text-muted">Professional templates and sample resumes</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {resumeInterviewResources.map((resource, index) => (
                  <ResourceCard key={index} resource={resource} index={index} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
